<template>
  <ModalStep
    :id="'mfa-login-modal-content'"
    :header="{
      title: t('auth.mfa.login.title'),
    }"
  >
    <template #bodyContent>
      <form>
        <p class="description-text">{{ t('auth.mfa.login.description') }}</p>
        <div v-if="isRateLimitedRef && countdownRef" class="rate-limit-warning" data-test-id="mfa-rate-limit-warning">
          <p>{{ t('auth.mfa.login.error.tooManyAttempts') }}</p>
          <p class="countdown">{{ t('auth.mfa.login.retryIn', { time: countdownRef }) }}</p>
        </div>
        <CustomInput
          id="mfa-login-code"
          data-test-id="mfa-login-code"
          type="text"
          :label="t('auth.mfa.login.input.code.label')"
          :placeholder="t('auth.mfa.login.input.code.placeholder')"
          :value="codeRef"
          @emit:change="codeChangeHandler"
          :errors="codeErrorRef"
          :maxlength="6"
          :disabled="isRateLimitedRef"
          autofocus
        />
      </form>
    </template>
    <template #footer>
      <div class="c-btns-container">
        <CustomButton
          usage="button"
          type="button"
          color="white"
          @emit:click="closeModal"
          :text="t('ui.button.cancel')"
        ></CustomButton>
        <CustomButton
          usage="button"
          type="button"
          data-test-id="confirm-mfa-login-button"
          color="primary"
          @emit:click="submitForm"
          :isDisabled="!codeRef || codeRef.length !== 6 || isRateLimitedRef"
          :isLoading="isPending"
          :text="t('ui.button.confirm')"
        ></CustomButton>
      </div>
    </template>
  </ModalStep>
</template>

<script setup lang="ts">
import { useMutation } from '@tanstack/vue-query'
import { computed, onMounted, onUnmounted, ref } from 'vue'

import { mfaVerifyLogin } from '@/features/auth/api'
import useLoginSession from '@/features/auth/hooks/use-login-session.hook'
import authStore from '@/features/auth/stores/auth/auth.store'
import programsStore from '@/features/programs/stores/programs/programs.store'
import CustomButton from '@/features/ui/components/button/custom-button.vue'
import CustomInput from '@/features/ui/components/input/custom-input.vue'
import ModalStep from '@/features/ui/components/modal/modal-step/modal-step.vue'
import modalsStore from '@/features/ui/store/modal/modal.store'
import useRouter from '@/hooks/use-router.hook'
import useToast from '@/hooks/use-toasts.hook'
import { useI18n } from '@/lib/i18n'
import { ANALYTICS_EVENTS, trackEvent } from '@/lib/posthog'
import { ROUTES } from '@/utils/config/constants'

const { t } = useI18n()
const toast = useToast()
const router = useRouter()
const loginSession = useLoginSession()

// KC-439 : affichage de la modale MFA (mesure le taux d'abandon MFA)
onMounted(() => {
  trackEvent(ANALYTICS_EVENTS.AUTH_MFA_PROMPT, {
    time_since_start_ms: loginSession.timeSinceStart(),
    failed_attempts_before: Math.max(0, loginSession.attemptCount.value - 1),
  })
})

const codeRef = ref<string>('')
const codeErrorRef = ref<string>('')
const isRateLimitedRef = ref<boolean>(false)
const countdownRef = ref<string>('')
let countdownInterval: ReturnType<typeof setInterval> | null = null

// Get email from modal attached data
const emailRef = computed(() => modalsStore.modals['mfa-login'].attachedData.email || '')

function startCountdown(retryAfter: string) {
  const retryTime = new Date(retryAfter).getTime()

  const updateCountdown = () => {
    const now = Date.now()
    const timeLeft = retryTime - now

    if (timeLeft <= 0) {
      isRateLimitedRef.value = false
      countdownRef.value = ''
      if (countdownInterval) {
        clearInterval(countdownInterval)
        countdownInterval = null
      }
      return
    }

    const minutes = Math.floor(timeLeft / 60000)
    const seconds = Math.floor((timeLeft % 60000) / 1000)
    countdownRef.value = `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  updateCountdown()
  countdownInterval = setInterval(updateCountdown, 1000)
}

const { mutate: verifyMfa, isPending } = useMutation({
  mutationFn: mfaVerifyLogin,
  onError: (error: any) => {
    if (error.status === 429) {
      isRateLimitedRef.value = true
      codeErrorRef.value = ''

      if (error.response?.retryAfter) {
        startCountdown(error.response.retryAfter)
      }

      toast?.error(t('auth.mfa.login.error.tooManyAttempts'))
    } else if (error.status === 400 || error.status === 401) {
      codeErrorRef.value = t('auth.mfa.login.error')
      toast?.error(t('auth.mfa.login.error'))
      trackEvent(ANALYTICS_EVENTS.AUTH_MFA_FAILURE, {
        mfa_attempt_number: loginSession.registerMfaAttempt(),
        time_since_start_ms: loginSession.timeSinceStart(),
      })
    } else {
      toast?.error(t('error.common'))
    }
  },
  onSuccess: async (data) => {
    await authStore.setAuthToken(data.token)
    trackEvent(ANALYTICS_EVENTS.AUTH_LOGIN_COMPLETE, {
      auth_method: 'email_mfa',
      time_to_login_ms: loginSession.timeSinceStart(),
      failed_attempts_in_session: Math.max(0, loginSession.attemptCount.value - 1),
      used_password_reset: loginSession.usedPasswordReset.value,
      is_first_login: data.isFirstLogin ?? false,
      program_id: programsStore.selectedProgramId,
      role: null,
      had_program_deep_link: loginSession.hadProgramDeepLink(),
      program_context_preserved: loginSession.programContextPreserved(),
    })
    loginSession.reset()
    modalsStore.toggleModal('mfa-login', false)
    router?.push(ROUTES.REDIRECT).catch(() => {})
  },
})

function codeChangeHandler(value: string) {
  codeRef.value = value
  codeErrorRef.value = ''
}

function submitForm() {
  if (!codeRef.value || codeRef.value.length !== 6) {
    codeErrorRef.value = t('auth.mfa.login.error')
    return
  }

  if (!emailRef.value) {
    toast?.error(t('error.common'))
    return
  }

  verifyMfa({
    email: emailRef.value,
    code: codeRef.value,
  })
}

function closeModal() {
  if (countdownInterval) {
    clearInterval(countdownInterval)
    countdownInterval = null
  }
  modalsStore.toggleModal('mfa-login', false)
}

onUnmounted(() => {
  if (countdownInterval) {
    clearInterval(countdownInterval)
  }
})
</script>

<style lang="scss" scoped>
.description-text {
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  margin-bottom: 1.5rem;
  text-align: center;
}

.rate-limit-warning {
  background-color: var(--color-error-light, #fee);
  border: 1px solid var(--color-error, #f44);
  border-radius: 4px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  text-align: center;

  p {
    margin: 0;
    color: var(--color-error, #f44);
    font-size: 0.9rem;

    &.countdown {
      font-size: 1.2rem;
      font-weight: 600;
      margin-top: 0.5rem;
    }
  }
}
</style>
